import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import CodiceFiscale from 'codice-fiscale-js';

import {
  NavigationItem,
  navigationItemsWithPatient,
} from '../../models/navigation-item-model';
import {
  PatientEssentialInfo,
  RegistryPatient,
} from '../../models/patient-model';
import { pages } from '../../shared/labels';
import { PatientService } from '../../services/patient.service';
import { LabelService } from '../../services/label.service';
import { ErrorPanelService } from '../../services/error-panel.service';
import { SidebarService } from '../../services/sidebar.service';
import { SharedModule } from '../../shared/shared.module';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';

@Component({
  selector: 'app-registry-page',
  standalone: true,
  imports: [
    SharedModule,
    CommonModule,
    FormsModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
  ],
  templateUrl: './registry-page.component.html',
  styleUrls: ['./registry-page.component.scss'],
})
export class RegistryPageComponent implements OnInit {
  navigationItems: NavigationItem[] = [];
  patientForm: FormGroup;
  patientId: string = '';
  isEditing: boolean = false;
  isFiscalCodeManuallyEdited: boolean = false;
  isPatientActive: boolean = false;
  constructor(
    private fb: FormBuilder,
    private patientService: PatientService,
    public labelService: LabelService,
    public errorPanelService: ErrorPanelService,
    private changeDetectorRef: ChangeDetectorRef,
    private route: ActivatedRoute,
    private sidebarService: SidebarService
  ) {
    this.patientForm = this.fb.group({
      first_name: ['', Validators.required],
      last_name: ['', Validators.required],
      mobile: [''],
      email: [''],
      gender: ['', [Validators.pattern(/^[MF]?$/)]], // Optional, but if filled, must be 'M' or 'F'
      date_of_birth: [''],
      place_of_birth: [''],
      province_of_birth: ['', [Validators.pattern(/^([A-Z]{2})?$/)]], // Optional, but if filled, must be 2 uppercase letters
      country_of_birth: [''],
      tax_code: [''],
    });
    this.patientForm.disable();

    // Listen for changes in the tax_code field
    this.patientForm.get('tax_code')?.valueChanges.subscribe(() => {
      this.isFiscalCodeManuallyEdited = true;
    });
  }

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.patientService
        .getPatientEssentialInfo(params['patientId'])
        .subscribe({
          next: (data: PatientEssentialInfo) => {
            this.patientId = data.id;
            this.isPatientActive = data.is_active;
            this.navigationItems = this.sidebarService.updateNavigationItems(
              navigationItemsWithPatient,
              data.first_name,
              data.last_name,
              this.patientId,
              pages.registry
            );
            this.getRegistryData();
          },
          error: (err) => {
            console.error('Error getting patient essential info:', err);
            this.errorPanelService.showErrorSnackBar(
              'Errore nel recuperare i dati del paziente'
            );
          },
        });
    });
  }

  getRegistryData() {
    this.patientService.getPatient(this.patientId).subscribe({
      next: (response: RegistryPatient) => {
        if (
          response.date_of_birth &&
          typeof response.date_of_birth === 'string'
        ) {
          const [day, month, year] = response.date_of_birth.split('/');
          response.date_of_birth = new Date(+year, +month - 1, +day);
        }

        this.patientForm.patchValue(response);
        this.isFiscalCodeManuallyEdited = false;
      },
      error: (error) => {
        console.error('Error fetching patient data', error);
        this.errorPanelService.showErrorSnackBar(
          'Errore nel recuperare i dati anagrafici del paziente'
        );
      },
    });
  }

  onEditClick(): void {
    this.isEditing = true;
    this.patientForm.enable();
    this.isFiscalCodeManuallyEdited = false; // Reset the flag when entering edit mode
  }

  onSaveClick(): void {
    if (this.patientForm.valid) {
      let updatedPatient: RegistryPatient = { ...this.patientForm.value };

      if (updatedPatient.date_of_birth) {
        const date = new Date(updatedPatient.date_of_birth);
        updatedPatient.date_of_birth = `${date
          .getDate()
          .toString()
          .padStart(2, '0')}/${(date.getMonth() + 1)
          .toString()
          .padStart(2, '0')}/${date.getFullYear()}`;
      }

      if (
        !this.isFiscalCodeManuallyEdited &&
        this.canCalculateFiscalCode(updatedPatient)
      ) {
        this.calculateFiscalCode(updatedPatient);
        updatedPatient.tax_code = this.patientForm.get('tax_code')?.value;
      }

      this.patientService.putPatient(this.patientId, updatedPatient).subscribe({
        next: (response) => {
          this.isEditing = false;
        },
        error: (error) => {
          console.error('Error updating patient', error);
          this.errorPanelService.showErrorSnackBar(
            "Errore nell'aggiornamento dei dati del paziente"
          );
        },
      });
      this.patientForm.disable();
    } else {
      this.errorPanelService.showErrorSnackBar(
        'Per favore correggi gli errori del form prima di salvare'
      );
    }
  }

  onCancelClick(): void {
    this.isEditing = false;
    this.patientForm.disable();
    this.getRegistryData(); // Reload original data
  }

  canCalculateFiscalCode(patient: RegistryPatient): boolean {
    return !!(
      patient.first_name &&
      patient.last_name &&
      patient.gender &&
      patient.date_of_birth &&
      patient.place_of_birth &&
      patient.province_of_birth
    );
  }

  calculateFiscalCode(patient: RegistryPatient): void {
    try {
      const cf = new CodiceFiscale({
        name: patient.first_name,
        surname: patient.last_name,
        gender: patient.gender === 'M' ? 'M' : 'F',
        day:
          typeof patient.date_of_birth === 'string'
            ? parseInt(patient.date_of_birth.split('/')[0], 10)
            : patient.date_of_birth.getDate(),
        month:
          typeof patient.date_of_birth === 'string'
            ? parseInt(patient.date_of_birth.split('/')[1], 10)
            : patient.date_of_birth.getMonth() + 1,
        year:
          typeof patient.date_of_birth === 'string'
            ? parseInt(patient.date_of_birth.split('/')[2], 10)
            : patient.date_of_birth.getFullYear(),
        birthplace: patient.place_of_birth,
        birthplaceProvincia: patient.province_of_birth,
      });
      this.patientForm.patchValue({ tax_code: cf.cf }, { emitEvent: false });
    } catch (error) {
      console.error('Error calculating fiscal code:', error);
      this.errorPanelService.showErrorSnackBar(
        'Errore nel calcolare il codice fiscale:'
      );
    }
  }
}
